import type {
  AccessExtensionParams,
  AdditionalPermissionsParams,
  MessageParams,
  RepairParams,
  ServiceRequestType,
  SupportParams,
} from '@smartsash/api-client';

/**
 * What a service request is about, in one short line (HS-1542): the Subject column on `/requests`,
 * the subject line of its mobile card and the dashboard alert queue's row title all read it here.
 *
 * Kept small and pure: the list's column definitions are in the initial bundle, so this reads the
 * one member it needs straight from `params` rather than through the detail page's params parser.
 */

/** A subject's length cap, the trailing ellipsis included (Jira HS-1542). */
export const REQUEST_SUBJECT_MAX_CHARS = 120;

const ELLIPSIS = '…';

/**
 * Cut `text` to `max` characters, the trailing "…" included. Counts code points, so an emoji at
 * the cut is kept or dropped whole, never split into half a surrogate pair.
 */
export function truncateSubject(text: string, max: number = REQUEST_SUBJECT_MAX_CHARS): string {
  const points = Array.from(text);
  if (points.length <= max) return text;
  return `${points
    .slice(0, max - 1)
    .join('')
    .trimEnd()}${ELLIPSIS}`;
}

/** The first line of `text` that has anything on it, trimmed. */
function firstLine(text: string | undefined): string | undefined {
  return text
    ?.split(/\r?\n/)
    .map((line) => line.trim())
    .find((line) => line !== '');
}

/** What the rule reads: a `ServiceRequestDto`, or anything with its `type` and raw `params`. */
export interface SubjectSource {
  type: string;
  params?: unknown;
}

/**
 * The free-text member that names a request of each type. Keyed by the generated type names and
 * checked against the generated params schemas, so a renamed member or a new type is a compile error.
 */
const SUBJECT_MEMBER = {
  Support: 'subject',
  Message: 'subject',
  Repair: 'description',
  AdditionalPermissions: 'reason',
  AccessExtension: 'message',
} as const satisfies Record<ServiceRequestType, string> & {
  Support: keyof SupportParams;
  Message: keyof MessageParams;
  Repair: keyof RepairParams;
  AdditionalPermissions: keyof AdditionalPermissionsParams;
  AccessExtension: keyof AccessExtensionParams;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The naming member's text, when `params` is an object and the member is a string. */
function subjectSource(dto: SubjectSource): string | undefined {
  if (!Object.hasOwn(SUBJECT_MEMBER, dto.type) || !isRecord(dto.params)) return undefined;
  const value = dto.params[SUBJECT_MEMBER[dto.type as ServiceRequestType]];
  return typeof value === 'string' ? value : undefined;
}

/**
 * The request's subject: Support and Message `subject`, Repair the first line of `description`,
 * AdditionalPermissions `reason`, AccessExtension `message` — its first non-blank line, trimmed and
 * cut to {@link REQUEST_SUBJECT_MAX_CHARS}. `null` when there is no text; each caller picks its own
 * fallback (the list shows "—", the alert queue names the type).
 */
export function requestSubject(dto: SubjectSource): string | null {
  const text = firstLine(subjectSource(dto));
  return text === undefined ? null : truncateSubject(text);
}
